import {
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import {
  Badge,
  Button,
  Card,
  DatePicker,
  Popover,
  Select,
  Space,
  Switch,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import {
  debounce,
  find,
  findIndex,
  flattenDeep,
  map,
  some,
  uniqBy,
} from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../common/api';
import {
  BACKEND_DATE_FORMAT,
  COLUMN_COLOR,
  ERROR_MESSAGE,
  LEARNING_TAG,
  LEVEL,
  LIMIT,
  PROJECT_COLUMN_TYPE,
  PUSHER_EVENTS,
  RESOURCE_ALLOCATION_FIELDS,
  ROLE_KEYS,
  ROUTES,
  ROW_COLOR,
  ROW_HEIGHT,
  SESSION_EXPIRED,
  SYNC_MESSAGE,
  USER,
  WEEK_FORMAT,
} from '../../common/constants';
import CommonSelect from '../../components/CommonSelect';
import EmptyComponents from '../../components/EmptyComponents';
import LoaderComponent from '../../components/LoaderComponent';
import {
  subscribeToResourceAllocationEvents,
  unsubscribeFromResourceAllocationEvents,
} from '../../components/PusherEvents';
import history from '../../historyData';
import { useTableQueryParams } from '../../hooks/useTableQueryParams';
import { ALL_TAGS, DEPARTMENTS } from '../users/graphql/Queries';
import HeaderComponent from './components/HeaderComponent';
import InputCellRenderer from './components/InputCellRenderer';
import NameCellRenderer from './components/NameCellRenderer';
import PlannedCellRenderer from './components/PlannedCellRenderer';
import {
  CREATE_RESOURCE_ALLOCATION,
  UPDATE_RESOURCE_ALLOCATION,
} from './graphql/Mutation';
import {
  ACTIVE_PROJECTS,
  PROJECTS,
  RESOURCE_ALLOCATION,
} from './graphql/Queries';
import './resourceAllocation.less';

function ResourceAllocationV3() {
  // eslint-disable-next-line no-undef
  const user = JSON.parse(localStorage.getItem(USER));
  const defaultSkip = 0;
  const projectStatus = 'ACTIVE';
  const { params: filters, setParams: setFilters } = useTableQueryParams({
    fields: RESOURCE_ALLOCATION_FIELDS,
  });
  const [searchString, setSearchString] = useState(filters?.department);
  const [startDate, setStartDate] = useState(filters?.weekStart);
  const [endDate, setEndDate] = useState(
    dayjs(filters?.weekStart)?.weekday(5)?.format(BACKEND_DATE_FORMAT),
  );
  const [weekPickerValue, setWeekPickerValue] = useState(
    dayjs(filters?.weekStart),
  );

  const [selectedProjects, setSelectedProjects] = useState(filters?.projects);
  const [projectFilterData, setProjectFilterData] = useState([]);
  const [projectData, setProjectData] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const pathName = history?.location?.pathname;
  const [isProjectDataEnd, setIsProjectDataEnd] = useState(false);
  const [skipData, setSkipData] = useState(0);
  const [search, setSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [departmentFilteredId, setDepartmentFilteredId] = useState(
    filters?.department,
  );
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState(filters?.tags);
  const [isShow, setIsShow] = useState(filters?.showPlanned);
  const [rowData, setRowData] = useState();
  const [createPusherData, setCreatePusherData] = useState({});
  const [updatePusherData, setUpdatePusherData] = useState({});
  const [columnDefs, setColumnDefs] = useState([]);
  const [inputColId, setInputColId] = useState(null);
  const [inputRowId, setInputRowId] = useState(null);
  const filteredColumns = useMemo(() => {
    if (selectedProjects?.length > 0) {
      const defaultColumns = columnDefs?.slice(0, 3);
      const remainingColumns = columnDefs
        ?.slice(3)
        ?.filter(({ field }) => selectedProjects?.includes(field))
        ?.map((column) => ({ ...column, width: 'auto', flex: 1 }));
      return [...defaultColumns, ...remainingColumns];
    }
    return columnDefs;
  }, [selectedProjects, columnDefs]);

  const [executeQuery, { loading: resourceLoading }] = useLazyQuery(
    RESOURCE_ALLOCATION,
    {
      fetchPolicy: 'network-only',
      onCompleted: ({ resourceAllocations }) =>
        setRowData(uniqBy(resourceAllocations, 'id') ?? []),
      onError: () => {},
    },
  );
  const [executeProjectQuery] = useLazyQuery(PROJECTS, {
    fetchPolicy: 'network-only',
    onCompleted(res) {
      const projects = res?.projectsResourceAllocation?.projects ?? [];
      const isDataEnd = projects?.length < LIMIT;
      setIsProjectDataEnd(isDataEnd);
      const newData = projects?.map((mappedProject) => ({
        value: mappedProject?.id,
        label: mappedProject?.name,
      }));
      setProjectData((prevData) => {
        const merged = uniqBy([...prevData, ...newData], 'value');
        // A project selected from a later page (before a refresh) may not be
        // in this page's results; keep paging until every selected id resolves
        // to a label, otherwise the filter chip falls back to showing the id.
        const hasMissingSelectedProject = selectedProjects?.some(
          (projectId) =>
            !merged?.some((option) => option?.value === projectId),
        );
        if (hasMissingSelectedProject && !isDataEnd) {
          const newSkip = merged?.length;
          executeProjectQuery({
            variables: {
              filters: { skip: newSkip, limit: LIMIT, status: projectStatus },
            },
          });
          setSkipData(newSkip);
        }
        return merged;
      });
    },
    onError: () => {},
  });

  const [updateMutation, { loading: updateLoading }] = useMutation(
    UPDATE_RESOURCE_ALLOCATION,
    {
      fetchPolicy: 'network-only',
      onCompleted: () => {
        setIsSaved(true);
      },
      onError: () => {},
    },
  );

  const [createMutation, { loading: createLoading }] = useMutation(
    CREATE_RESOURCE_ALLOCATION,
    {
      fetchPolicy: 'network-only',
      onCompleted: () => {
        setIsSaved(true);
      },
      onError: () => {},
    },
  );

  const handleUpdateRecord = async (
    newValue,
    projectId,
    record,
    projectName,
  ) => {
    try {
      const hoursValue = parseInt(newValue, 10);
      const existingRecord = record?.hoursAllocation?.find(
        (item) => item?.projectId === projectId,
      );
      if (existingRecord) {
        const updatedRecord = {
          ...existingRecord,
          hours: hoursValue,
        };
        setIsSaved(false);
        await updateMutation({
          variables: {
            input: {
              hours: updatedRecord?.hours,
              id: updatedRecord?.id,
            },
          },
        });
      } else {
        const createdRecord = {
          projectId,
          projectName,
          hours: hoursValue,
        };
        setIsSaved(false);
        await createMutation({
          variables: {
            input: {
              hours: createdRecord?.hours,
              projectId: createdRecord?.projectId,
              resourceAllocationId: record?.id,
            },
          },
        });
      }
    } catch (errInfo) {
      // eslint-disable-next-line no-console
      console?.error('Save failed:', errInfo);
    }
  };

  const [activeProjectColumns, { loading: projectLoading }] = useLazyQuery(
    ACTIVE_PROJECTS,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        if (selectedItems?.length > 0) {
          const newData = res?.activeProjects?.map((mappedProject) => ({
            value: mappedProject?.id,
            label: mappedProject?.name,
          }));
          setProjectFilterData(() => uniqBy([...newData], 'value'));
        }
        const newData = res?.activeProjects?.map((mappedProject) => ({
          headerComponent: HeaderComponent,
          headerComponentParams: {
            headerName: mappedProject?.name,
            field: mappedProject?.id,
            pathName,
            weekPickerValue,
            startDate,
            endDate,
          },
          pinned: mappedProject?.tags?.includes(LEARNING_TAG) ? 'right' : '',
          autoHeaderHeight: true,
          field: mappedProject?.id,
          headerClass: find(mappedProject?.tags, (tag) =>
            some(PROJECT_COLUMN_TYPE, { name: tag }),
          ),
          cellClass: 'resource-cell-align',
          wrapText: true,
          minWidth: 60,
          width: selectedItems?.length > 0 || isShow ? 'auto' : 60,
          flex: selectedItems?.length > 0 || isShow ? 1 : 'auto',
          cellRenderer: (params) => (
            <InputCellRenderer
              result={weekPickerValue}
              handleUpdateRecord={handleUpdateRecord}
              startDate={startDate}
              endDate={endDate}
              {...params}
            />
          ),
          resizable: true,
        }));
        const defaultColumns = [
          {
            headerName: 'Name',
            field: 'user.displayName',
            width: 200,
            pinned: 'left',
            lockPinned: true,
            editable: false,
            headerClass: 'header-text-center',
            cellClass: 'lock-pinned',
            suppressMenu: false,
            resizable: false,
            cellRenderer: (params) => (
              <NameCellRenderer
                pathName={pathName}
                weekPickerValue={weekPickerValue}
                startDate={startDate}
                endDate={endDate}
                {...params}
              />
            ),
          },
          {
            headerName: 'Total',
            field: 'totalHours',
            headerClass: 'header-text-center',
            maxWidth: 80,
            pinned: 'left',
            lockPinned: true,
            editable: false,
            suppressMenu: false,
            resizable: false,
            cellClass:
              'lock-pinned resource-cell-align name-column-displayName',
          },
          {
            headerName: 'Planned',
            field: 'plannedHours',
            headerClass: 'header-text-center',
            maxWidth: 80,
            pinned: 'left',
            lockPinned: true,
            resizable: false,
            editable: false,
            suppressMenu: false,
            cellClass:
              'lock-pinned resource-cell-align name-column-displayName',
            cellRenderer: (params) => <PlannedCellRenderer {...params} />,
          },
        ];
        setColumnDefs(() => uniqBy([...defaultColumns, ...newData], 'field'));
      },
      onError: () => {},
    },
  );

  useEffect(() => {
    activeProjectColumns({
      variables: {
        filters: {
          startDate,
          ...(selectedItems?.length > 0 && { searchString: selectedItems }),
          isShow,
        },
      },
    });
    executeQuery({
      variables: {
        filters: {
          searchString,
          startDate,
          endDate,
          tagSearch: selectedItems?.length > 0 ? selectedItems : [],
        },
      },
    });
    executeProjectQuery({
      variables: {
        filters: {
          skip: defaultSkip,
          limit: LIMIT,
          status: projectStatus,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = (pusherResponse) => {
    const newData = { ...pusherResponse };
    setCreatePusherData(newData);
  };

  const handleUpdate = (pusherResponse) => {
    const newData = { ...pusherResponse };
    setUpdatePusherData(newData);
  };

  const updateRowData = useCallback(
    (newData, type) => {
      if (type === 'create') {
        const tempRowData = [...rowData];
        const index = findIndex(
          tempRowData,
          (record) => record?.id === newData?.resourceAllocationId,
        );
        if (index >= 0) {
          const currentData = { ...tempRowData[index] };
          const hoursAllocation = [
            ...(currentData?.hoursAllocation || []),
            {
              id: newData?.resourceProjectAllocationId,
              hours: newData?.hours,
              projectId: newData?.projectId,
            },
          ];
          tempRowData?.splice(index, 1, {
            ...currentData,
            plannedHours: newData?.plannedHours,
            hoursAllocation,
          });
          setRowData([...tempRowData]);
        }
      } else {
        const tempRowData = [...rowData];
        const index = findIndex(
          tempRowData,
          (record) => record?.id === newData?.resourceAllocationId,
        );
        if (index >= 0) {
          const currentData = { ...tempRowData[index] };
          const hoursAllocation = Array?.isArray(currentData?.hoursAllocation)
            ? currentData?.hoursAllocation?.map((allocation) => {
                if (allocation?.id === newData?.resourceProjectAllocationId) {
                  return { ...allocation, hours: newData?.hours };
                }
                return allocation;
              })
            : [];
          tempRowData?.splice(index, 1, {
            ...currentData,
            plannedHours: newData?.plannedHours,
            hoursAllocation,
          });
          setRowData([...tempRowData]);
        }
      }
    },
    [updatePusherData, createPusherData, rowData],
  );

  useEffect(() => {
    const channel = subscribeToResourceAllocationEvents({
      channelName: PUSHER_EVENTS.RESOURCE_ALLOCATION,
      startDate,
      endDate,
      handlers: {
        handleUpdate,
        handleCreate,
      },
    });

    return () => {
      unsubscribeFromResourceAllocationEvents(channel, startDate, endDate);
    };
  }, [startDate, endDate, columnDefs]);

  useEffect(() => {
    if (createPusherData?.resourceAllocationId) {
      updateRowData({ ...createPusherData }, 'create');
      setCreatePusherData({});
    }
  }, [createPusherData, updateRowData]);

  useEffect(() => {
    if (updatePusherData?.resourceAllocationId) {
      updateRowData({ ...updatePusherData }, 'update');
      setUpdatePusherData({});
    }
  }, [updatePusherData, updateRowData]);

  const handleDatepicker = (date) => {
    const weekStartDate = dayjs(date)?.weekday(1)?.format(BACKEND_DATE_FORMAT);
    const weekEndDate = dayjs(date)?.weekday(5)?.format(BACKEND_DATE_FORMAT);
    setWeekPickerValue(date);
    setStartDate(weekStartDate);
    setEndDate(weekEndDate);
    setFilters({ weekStart: weekStartDate });
    const queryVariables = {
      filters: {
        startDate: weekStartDate,
        endDate: weekEndDate,
        searchString: searchString?.length > 0 ? searchString : [],
        tagSearch: selectedItems?.length > 0 ? selectedItems : [],
      },
    };
    executeQuery({
      variables: queryVariables,
    });
    activeProjectColumns({
      variables: {
        filters: {
          startDate: weekStartDate,
          ...(selectedItems?.length > 0 && { searchString: selectedItems }),
          isShow,
        },
      },
    });
  };

  const prevWeek = () => {
    const weekStart = dayjs(weekPickerValue)?.weekday(-6);
    const weekEnd = dayjs(weekPickerValue)?.weekday(-2);
    const formattedStartDate = weekStart?.format(BACKEND_DATE_FORMAT);
    const formattedEndDate = weekEnd?.format(BACKEND_DATE_FORMAT);
    setWeekPickerValue(weekStart);
    setStartDate(formattedStartDate);
    setEndDate(formattedEndDate);
    setFilters({ weekStart: formattedStartDate });
    executeQuery({
      variables: {
        filters: {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          searchString: searchString?.length > 0 ? searchString : [],
          tagSearch: selectedItems?.length > 0 ? selectedItems : [],
        },
      },
    });
    activeProjectColumns({
      variables: {
        filters: {
          startDate: formattedStartDate,
          ...(selectedItems?.length > 0 && { searchString: selectedItems }),
          isShow,
        },
      },
    });
  };

  const nextWeek = () => {
    const newWeekStart = dayjs(weekPickerValue)?.weekday(8);
    const newWeekEnd = dayjs(weekPickerValue)?.weekday(12);
    const formattedNewStartDate = newWeekStart?.format(BACKEND_DATE_FORMAT);
    const formattedNewEndDate = newWeekEnd?.format(BACKEND_DATE_FORMAT);
    setWeekPickerValue(newWeekStart);
    setStartDate(formattedNewStartDate);
    setEndDate(formattedNewEndDate);
    setFilters({ weekStart: formattedNewStartDate });
    executeQuery({
      variables: {
        filters: {
          startDate: formattedNewStartDate,
          endDate: formattedNewEndDate,
          searchString: searchString?.length > 0 ? searchString : [],
          tagSearch: selectedItems?.length > 0 ? selectedItems : [],
        },
      },
    });
    activeProjectColumns({
      variables: {
        filters: {
          startDate: formattedNewStartDate,
          ...(selectedItems?.length > 0 && { searchString: selectedItems }),
          isShow,
        },
      },
    });
  };

  const handleDepartment = (value) => {
    const departmentValues = value?.length > 0
      ? value?.toString()?.trim()?.split(',')
      : [];
    setDepartmentFilteredId(value);
    setSearchString(departmentValues);
    setDepartmentSearch('');
    setFilters({ department: departmentValues });
    const formattedStartDate = weekPickerValue
      ?.weekday(1)
      ?.format(BACKEND_DATE_FORMAT);
    const formattedEndDate = weekPickerValue
      ?.weekday(5)
      ?.format(BACKEND_DATE_FORMAT);
    executeQuery({
      variables: {
        filters: {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          searchString: departmentValues,
          tagSearch: selectedItems?.length > 0 ? selectedItems : [],
        },
      },
    });
    activeProjectColumns({
      variables: {
        filters: {
          startDate: formattedStartDate,
          ...(selectedItems?.length > 0 && { searchString: selectedItems }),
          isShow,
        },
      },
    });
  };

  const handleDepartmentSearch = debounce((value) => {
    setDepartmentSearch(value);
  }, 500);

  const handlePopupScroll = async (e) => {
    const nearBottom =
      e?.target?.scrollTop + e?.target?.clientHeight ===
      e?.target?.scrollHeight;
    if (nearBottom && !isProjectDataEnd) {
      const newSkip = skipData + LIMIT;
      await executeProjectQuery({
        variables: {
          filters: {
            skip: newSkip,
            limit: LIMIT,
            status: projectStatus,
          },
        },
      });
      setSkipData(newSkip);
    }
  };

  const handleSearchData = debounce((value) => {
    executeProjectQuery({
      variables: {
        filters: {
          skip: defaultSkip,
          limit: LIMIT,
          searchString: value,
          status: projectStatus,
        },
      },
    });
  }, 700);

  const handleTagChange = (selectedTags) => {
    setSearch('');
    setTagSearch('');
    setSelectedItems(selectedTags);
    setFilters({ tags: selectedTags });
    executeQuery({
      variables: {
        filters: {
          startDate,
          endDate,
          searchString: searchString?.length > 0 ? searchString : [],
          tagSearch: selectedTags?.length > 0 ? selectedTags : [],
        },
      },
    });
    activeProjectColumns({
      variables: {
        filters: {
          startDate,
          ...(selectedTags?.length > 0 && { searchString: selectedTags }),
          isShow,
        },
      },
    });
  };

  const debouncedUpdateValue = useCallback(
    debounce((newValue) => {
      setSkipData(0);
      setTagSearch(newValue?.toLowerCase());
    }, 500),
    [],
  );

  const updateSearchValue = (newValue) => {
    setSearch(newValue?.toLowerCase());
  };

  const handleTagSearch = (searchText) => {
    debouncedUpdateValue(searchText);
    updateSearchValue(searchText);
  };

  const handleProjectSelect = (value) => {
    setSelectedProjects(value);
    setFilters({ projects: value });
    executeProjectQuery({
      variables: {
        filters: {
          skip: defaultSkip,
          limit: LIMIT,
          status: projectStatus,
        },
      },
    });
  };
  const renderMaxTag = (omittedValues) => {
    if (omittedValues?.length === 0) {
      return null;
    }
    return <span>+ {omittedValues?.length} more...</span>;
  };

  const customWeekStartEndFormat = (value) =>
    `${dayjs(value)?.weekday(1)?.format(WEEK_FORMAT)} - ${dayjs(value)
      ?.weekday(5)
      ?.format(WEEK_FORMAT)}`;

  const handleBeforeUnload = () => {
    history.push({
      pathname: history.location.pathname,
      state: null,
    });
  };

  useEffect(() => {
    // eslint-disable-next-line no-undef
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // eslint-disable-next-line no-undef
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleSync = async () => {
    const syncUrls = [
      process.env.REACT_APP_SYNC_LEAVES_URL,
      process.env.REACT_APP_SYNC_DASHBOARD_LEAVES_URL,
      process.env.REACT_APP_SYNC_USERS_URL,
      process.env.REACT_APP_SYNC_DEPARTMENT_URL,
    ];
    try {
      await Promise?.all(
        syncUrls?.map(async (url) => {
          await api?.post(url);
        }),
      );
      message?.destroy();
      message?.success(SYNC_MESSAGE);
    } catch (error) {
      if (
        error?.response?.data?.errors?.[0]?.extensions?.code === SESSION_EXPIRED
      ) {
        message?.destroy();
        message?.error(ERROR_MESSAGE);
        history?.replace({
          pathname: ROUTES?.AUTHENTICATION,
          // eslint-disable-next-line no-undef
          state: { from: window?.location?.pathname },
        });
      }
    }
  };

  const handleToggleSwitch = (checked) => {
    setIsShow(!checked);
    setFilters({ showPlanned: !checked });
    activeProjectColumns({
      variables: {
        filters: {
          startDate,
          ...(selectedItems?.length > 0 && { searchString: selectedItems }),
          isShow: !checked,
        },
      },
    });
  };

  const getRowClass = (params) => {
    const leave =
      params?.data?.leaves && map(flattenDeep(params?.data?.leaves), 'status');
    if (
      leave?.includes(ROW_COLOR.APPROVED) ||
      leave?.includes(ROW_COLOR.APPROVAL_IN_PROCESS)
    ) {
      return 'approved-leave-row-data';
    }
    if (leave?.includes(ROW_COLOR.PENDING)) {
      return 'pending-leave-row-data';
    }
    return null;
  };

  const noRowsOverlayComponent = useMemo(() => EmptyComponents, []);

  const highlightColumn = (colId, color) => {
    // eslint-disable-next-line no-undef
    const cells = document?.querySelectorAll(`.ag-cell[col-id="${colId}"]`);
    cells?.forEach((cell) => {
      // eslint-disable-next-line no-param-reassign
      cell.style.backgroundColor = color;
    });
  };

  const handleCellKey = (e) => {
    setInputColId(e?.column?.colId);
    setInputRowId(e?.rowIndex);
  };

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const cellElement = document?.querySelector('.ag-cell-focus');
    if (cellElement) {
      const childElements = cellElement?.children;
      if (
        childElements?.length === 1 &&
        childElements[0]?.tagName?.toLowerCase() === 'input'
      ) {
        const inputElement = childElements[0];
        inputElement?.focus();
      }
    }
  }, [inputColId, inputRowId]);

  return (
    <div className="table-card-page">
      <div className="resource-allocation">
        <Card
          className="ant-body-scroll"
          title={
            <span>
              Resource Allocation-v3{' '}
              {(updateLoading || createLoading) && (
                <span className="saving-message">Saving...</span>
              )}
              {isSaved && <span className="saved-message">Saved</span>}
            </span>
          }
          extra={
            <div className="resource-allocation-card">
              <div className="project-color-info">
                <Popover
                  placement="rightTop"
                  title="Project Color Info"
                  content={
                    <Space direction="vertical">
                      {Object.entries(PROJECT_COLUMN_TYPE)?.map(
                        ([key, type]) => (
                          <Badge
                            key={key}
                            className="project-info-badge"
                            color={type?.color}
                            text={type?.name}
                          />
                        ),
                      )}
                    </Space>
                  }
                >
                  <InfoCircleOutlined />
                </Popover>
              </div>
              <div className="resourceAllocation-date-picker">
                <div className="d-flex">
                  <LeftOutlined onClick={prevWeek} />
                  <DatePicker
                    suffixIcon={null}
                    value={weekPickerValue}
                    onChange={handleDatepicker}
                    format={customWeekStartEndFormat}
                    picker="week"
                    bordered={false}
                  />
                  <RightOutlined onClick={nextWeek} />
                </div>
              </div>
              {([ROLE_KEYS?.SUPER_ADMIN, ROLE_KEYS?.ADMIN]?.includes(
                user?.role,
              ) ||
                [LEVEL?.L1, LEVEL?.L2]?.includes(user?.level?.name)) && (
                <Tooltip
                  placement="bottom"
                  title={
                    <span>
                      Users, Department, Leaves, Dashboard-Leaves Sync
                    </span>
                  }
                >
                  <Button onClick={handleSync}>
                    <SyncOutlined />
                  </Button>
                </Tooltip>
              )}
              <span className="toggle-project-name">
                <Switch
                  checkedChildren="All"
                  unCheckedChildren="Planned"
                  checked={!isShow}
                  onChange={handleToggleSwitch}
                />
              </span>
              <span className="visible-project-name">
                <Select
                  mode="multiple"
                  placeholder="Select Project Columns"
                  className="visible-project-columns"
                  onChange={handleProjectSelect}
                  value={selectedProjects}
                  maxTagCount={3}
                  maxTagPlaceholder={renderMaxTag}
                  options={
                    selectedItems?.length > 0 ? projectFilterData : projectData
                  }
                  allowClear
                  filterOption={(inputValue, option) =>
                    option?.label
                      ?.toLowerCase()
                      ?.includes(inputValue?.toLowerCase())
                  }
                  onPopupScroll={handlePopupScroll}
                  onSearch={(e) => {
                    handleSearchData(e);
                    setSkipData(0);
                  }}
                  virtual={false}
                />
              </span>
              <span className="resource-allocation-select">
                <>
                  <CommonSelect
                    mode="tags"
                    placeholder="Select Name, Department, Designation"
                    className="resource-allocation-search"
                    query={DEPARTMENTS}
                    onChange={handleDepartment}
                    onSearch={handleDepartmentSearch}
                    variables={{ search: departmentSearch }}
                    useEffectDeps={[departmentSearch]}
                    responsePath="departments.departments"
                    valuePath="name"
                    labelPath="name"
                    fetchPolicy="network-only"
                    maxTagCount={3}
                    maxTagPlaceholder={renderMaxTag}
                    allowClear
                    onClear={() => setSearchString([])}
                    value={departmentFilteredId}
                    virtual={false}
                  />
                </>
              </span>
              <span className="resource-allocation-select">
                <CommonSelect
                  mode="tags"
                  placeholder="Search Tags"
                  className="resource-allocation-tags"
                  allowClear
                  query={ALL_TAGS}
                  onChange={handleTagChange}
                  onSearch={(searchText) => {
                    handleTagSearch(searchText);
                  }}
                  variables={{
                    searchString: tagSearch,
                  }}
                  useEffectDeps={[tagSearch]}
                  responsePath="searchTags.tags"
                  valuePath="name"
                  labelPath="name"
                  fetchPolicy="network-only"
                  maxTagCount={3}
                  maxTagPlaceholder={renderMaxTag}
                  value={selectedItems}
                  searchValue={search}
                  virtual={false}
                />
              </span>
            </div>
          }
        >
          <div className="card-body-wrapper">
            {resourceLoading || projectLoading ? (
              <LoaderComponent size="default" />
            ) : (
              <div className="container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    rowData={rowData}
                    rowHeight={ROW_HEIGHT}
                    columnDefs={filteredColumns}
                    noRowsOverlayComponent={noRowsOverlayComponent}
                    getRowClass={getRowClass}
                    onCellFocused={handleCellKey}
                    onCellMouseOver={(e) => {
                      highlightColumn(e?.colDef?.field, COLUMN_COLOR);
                    }}
                    onCellMouseOut={(e) => {
                      highlightColumn(e?.colDef?.field, '');
                    }}
                    suppressScrollOnNewData
                  />
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default ResourceAllocationV3;
